/**
 * Workspace preference for dual-role users (CRM staff who are also
 * trainers/interpreters with the same email).
 *
 * - crm: always open the main Rapid Cert CRM
 * - instructor / interpreter: open that portal after login
 * - unset: CRM staff (admin/owner or crm_access) stay in CRM;
 *   dedicated portal accounts still redirect by email match
 *
 * Session override (sessionStorage) lets dual-role users switch panels
 * for the current browser tab without changing their account defaults.
 */
export type PreferredWorkspace = 'crm' | 'instructor' | 'interpreter';

const OVERRIDE_KEY = 'rapidcert_workspace_override';

type WorkspaceUser = {
  app_metadata?: Record<string, unknown>;
  user_metadata?: Record<string, unknown>;
} | null;

export function getPreferredWorkspace(user: WorkspaceUser): PreferredWorkspace | null {
  if (!user) return null;
  const raw =
    (user.app_metadata?.preferred_workspace as string | undefined) ||
    (user.user_metadata?.preferred_workspace as string | undefined);
  if (raw === 'crm' || raw === 'instructor' || raw === 'interpreter') return raw;
  return null;
}

export function getWorkspaceOverride(): PreferredWorkspace | null {
  try {
    const raw = sessionStorage.getItem(OVERRIDE_KEY);
    if (raw === 'crm' || raw === 'instructor' || raw === 'interpreter') return raw;
  } catch {
    /* ignore */
  }
  return null;
}

export function setWorkspaceOverride(workspace: PreferredWorkspace | null): void {
  try {
    if (!workspace) sessionStorage.removeItem(OVERRIDE_KEY);
    else sessionStorage.setItem(OVERRIDE_KEY, workspace);
  } catch {
    /* ignore */
  }
}

/** CRM staff should not be forced into trainer/interpreter panels on email match. */
export function prefersCrmWorkspace(user: WorkspaceUser): boolean {
  if (!user) return false;
  const override = getWorkspaceOverride();
  if (override === 'crm') return true;
  if (override === 'instructor' || override === 'interpreter') return false;

  const pref = getPreferredWorkspace(user);
  if (pref === 'crm') return true;
  if (pref === 'instructor' || pref === 'interpreter') return false;
  if (user.app_metadata?.crm_access === true) return true;
  const role =
    (user.app_metadata?.role as string | undefined) ||
    (user.user_metadata?.role as string | undefined);
  // Staff CRM accounts use admin/owner; portal-only trainer logins use role "user"
  if (role === 'admin' || role === 'owner') return true;
  return false;
}

export function prefersInstructorWorkspace(user: WorkspaceUser): boolean {
  const override = getWorkspaceOverride();
  if (override === 'instructor') return true;
  if (override === 'crm' || override === 'interpreter') return false;

  const pref = getPreferredWorkspace(user);
  if (pref === 'instructor') return true;
  if (pref === 'crm') return false;
  return user?.app_metadata?.portal_only === true || user?.app_metadata?.portal_role === 'instructor';
}

export function prefersInterpreterWorkspace(user: WorkspaceUser): boolean {
  const override = getWorkspaceOverride();
  if (override === 'interpreter') return true;
  if (override === 'crm' || override === 'instructor') return false;

  const pref = getPreferredWorkspace(user);
  if (pref === 'interpreter') return true;
  if (pref === 'crm') return false;
  return user?.app_metadata?.portal_only === true || user?.app_metadata?.portal_role === 'interpreter';
}

/** True when this login can use the main CRM (dual-role switch target). */
export function canAccessCrmWorkspace(user: WorkspaceUser): boolean {
  if (!user) return false;
  const pref = getPreferredWorkspace(user);
  if (pref === 'crm') return true;
  if (user.app_metadata?.crm_access === true) return true;
  const role =
    (user.app_metadata?.role as string | undefined) ||
    (user.user_metadata?.role as string | undefined);
  return role === 'admin' || role === 'owner';
}
